import { useEffect, useMemo, useRef, useState } from 'react'

import { type Timestamp } from '@bufbuild/protobuf/wkt'

import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import { type CivilDate, civilDateToInputValue } from '~/models/civilDate'
import {
  type DraftCell,
  type NoticeDraft,
  type NoticeGridPoint,
  draftFromPoints,
  findInvalidCells,
  isDraftDirty,
} from '~/pages/SplitSupplyPlanDetail/models/draft'

const EMPTY_DRAFT: NoticeDraft = []

type Params<P extends NoticeGridPoint> = {
  /** サーバーが返した地点 (表示している範囲の全地点)。 */
  fetchedPoints: readonly P[]
  /**
   * 取得した最終更新 (楽観ロックの基準にするもの)。 編集を始めた時点のものを `baseline` として保つ。
   * 地点ごとに基準を持つ自社の通告値は渡さない。
   */
  updatedAt?: Timestamp | undefined
  /** 月全体の表示か (1 日の表示か)。 */
  wholeMonth: boolean
  /** 表示している受渡日。 */
  deliveryDate: CivilDate | undefined
  /**
   * true になった時に 1 回だけ、 「編集」 を押した後と同じ状態で開く (「追加」 で作った直後に、 取得できていれば true にする)。
   * 自社の通告値のように最初から編集で開かない画面は渡さない。
   */
  startEditingWhen?: boolean
  /** `startEditingWhen` で編集を始めた後に呼ぶ (開く合図の route state を消す)。 参照が変わってもよい。 */
  onStartedEditing?: () => void
}

type Result<P extends NoticeGridPoint> = {
  /** 表に出す地点。 編集中は編集を始めた時点の地点 (取得し直されても変えない)。 */
  points: readonly P[]
  isEditing: boolean
  /** 表に出す値 (編集中は編集中の値、 そうでなければ保存済みの値)。 */
  draft: NoticeDraft
  /** 編集を始めた時点の保存済みの値。 */
  original: NoticeDraft
  /** 編集を始めた時点の最終更新 (楽観ロックの基準)。 編集中でなければ undefined。 */
  baseline: Timestamp | undefined
  isDirty: boolean
  invalidCells: DraftCell[]
  guard: ReturnType<typeof useUnsavedGuard>
  startEditing: () => void
  /** 編集を終える (破棄・保存成功)。 */
  finishEditing: () => void
  setEdited: (next: NoticeDraft) => void
}

/**
 * 通告値の詳細 (自社の通告値・他社の通告値) の編集の状態。 編集を始めた時点の地点と最終更新を
 * まとめて保ち (値と楽観ロックの基準)、 編集中に取得し直されても変えない (他の人の保存があれば保存で競合になる)。
 * 見ている範囲 (日・1 日 / 月全体) が変わったら編集は終わる。
 */
export function useNoticeDraftEditing<P extends NoticeGridPoint>({
  fetchedPoints,
  updatedAt,
  wholeMonth,
  deliveryDate,
  startEditingWhen = false,
  onStartedEditing,
}: Params<P>): Result<P> {
  const scope = `${wholeMonth ? 'month' : 'day'}|${civilDateToInputValue(deliveryDate)}`
  const [isEditing, setEditing] = useState(false)
  const [editBase, setEditBase] = useState<readonly P[] | undefined>(undefined)
  const [baseline, setBaseline] = useState<Timestamp | undefined>(undefined)
  const points = isEditing && editBase !== undefined ? editBase : fetchedPoints
  const original = useMemo(() => draftFromPoints(points), [points])
  const [edited, setEdited] = useState<NoticeDraft>(EMPTY_DRAFT)
  const [shownScope, setShownScope] = useState(scope)
  const [started, setStarted] = useState(false)

  const begin = (): void => {
    setEditBase(fetchedPoints)
    setBaseline(updatedAt)
    setEdited(draftFromPoints(fetchedPoints))
    setEditing(true)
  }
  const reset = (): void => {
    setEditing(false)
    setEditBase(undefined)
    setBaseline(undefined)
    setEdited(EMPTY_DRAFT)
  }

  if (shownScope !== scope) {
    setShownScope(scope)
    reset()
  }
  if (startEditingWhen && !started) {
    setStarted(true)
    begin()
  }
  // 呼び出し側の関数の参照が変わっても効果をやり直さない (開始の合図は 1 回だけ)
  const onStartedRef = useRef(onStartedEditing)
  useEffect(() => {
    onStartedRef.current = onStartedEditing
  })
  useEffect(() => {
    if (started) onStartedRef.current?.()
  }, [started])

  const draft = isEditing ? edited : original
  const isDirty = isEditing && isDraftDirty(original, draft)
  const invalidCells = useMemo(() => findInvalidCells(draft), [draft])
  const guard = useUnsavedGuard({ isDirty })

  return {
    points,
    isEditing,
    draft,
    original,
    baseline: isEditing ? baseline : undefined,
    isDirty,
    invalidCells,
    guard,
    startEditing: begin,
    finishEditing: reset,
    setEdited,
  }
}
